import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Megaphone, Trash2, Mail } from "lucide-react";

const EMPTY = { title: "", body: "", send_email: true };

export const AnnouncementsDialog = ({ course, onClose }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { if (course) api.get(`/lms/admin/courses/${course.id}/announcements`).then(({ data }) => setItems(data.announcements || [])).catch(() => {}); }, [course]);
  useEffect(() => { setForm(EMPTY); load(); }, [load]);
  if (!course) return null;

  const post = async () => {
    setBusy(true);
    try {
      await api.post(`/lms/admin/courses/${course.id}/announcements`, form);
      toast.success(form.send_email ? "Announcement posted and emailing learners" : "Announcement posted");
      setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't post"); }
    finally { setBusy(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/lms/admin/announcements/${a.id}`).catch(() => toast.error("Couldn't delete")); load();
  };

  return (
    <Dialog open={!!course} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="announcements-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone size={18} /> Announcements · {course.title}</DialogTitle>
          <DialogDescription className="text-xs">Shown on every enrolled learner's dashboard. Optionally emailed to active learners too.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Assessment day moved to Friday" data-testid="announcement-title" /></div>
          <div><Label className="text-xs">Message</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="min-h-[100px]" data-testid="announcement-body" /></div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="announcement-send-email" /> Also email enrolled learners</label>
            <Button size="sm" className="bg-green-700 hover:bg-green-800 text-white" disabled={busy || !form.title || !form.body} onClick={post} data-testid="announcement-post">Post announcement</Button>
          </div>
        </div>
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="rounded-lg border border-border bg-white p-3" data-testid={`announcement-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">{a.title}</p>
                  <p className="text-[11px] text-muted-foreground">{a.author_name} · {new Date(a.created_at).toLocaleString()}{a.emailed ? <span className="inline-flex items-center gap-1"> · <Mail size={10} /> {a.emailed} emailed</span> : ""}</p>
                </div>
                <button onClick={() => remove(a)} className="text-rose-600 hover:text-rose-800" data-testid={`announcement-delete-${a.id}`}><Trash2 size={13} /></button>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{a.body}</p>
            </div>
          ))}
          {items.length === 0 && <p className="py-4 text-center text-sm italic text-muted-foreground">No announcements yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
